/**
 * Sentry frontend error tracking.
 *
 * Opt-in via REACT_APP_SENTRY_DSN. If the env var is empty (preview/dev),
 * we skip init entirely — Sentry stays quiet so we don't pollute the
 * production project with dev noise.
 *
 * Captures:
 *   - Unhandled JS errors
 *   - Unhandled promise rejections
 *   - React render errors (via <SentryErrorBoundary> wrapping App)
 *   - User context (id + email) once an admin or player logs in
 */
// NAMED imports only — deliberately not `import * as Sentry`. Re-exporting the
// whole namespace (and reaching members off it at runtime) makes every export
// "used" as far as webpack can tell, so replay + rrweb + feedback were shipped
// in the entry chunk even though we only configure browser tracing. That was
// ~168 KB of dead code on every first page load.
import { ErrorBoundary, browserTracingIntegration, captureException, init, setUser } from '@sentry/react';

const DSN = (process.env.REACT_APP_SENTRY_DSN || '').trim();

export const sentryEnabled = Boolean(DSN);

export function initSentry() {
  if (!sentryEnabled) return;
  init({
    dsn: DSN,
    environment: process.env.REACT_APP_SENTRY_ENV || 'production',
    release: process.env.REACT_APP_SENTRY_RELEASE || undefined,
    // Browser tracing — automatic perf instrumentation for page loads,
    // SPA navigations, fetch/XHR calls, and long tasks. Catches mobile
    // jank (long-running scroll handlers, heavy renders) and slow API
    // calls (e.g. ESPN leaderboard hangs) without any per-callsite code.
    // Combined with `tracesSampleRate` below for free-tier-friendly volume.
    integrations: [browserTracingIntegration()],
    // 20% perf trace sampling — gives a useful picture without blowing
    // through Sentry's free tier on a busy tournament weekend.
    tracesSampleRate: 0.2,
    // Don't send PII headers / IPs by default — we only attach the
    // user's UUID + email via setUser() below, nothing else.
    sendDefaultPii: false,
  });
}

/** Attach the current user to all subsequent events. Safe to call anywhere. */
export function setSentryUser(user) {
  if (!sentryEnabled || !user) return;
  setUser({ id: user.id, email: user.email });
}

/** Clear user context (call on logout). */
export function clearSentryUser() {
  if (!sentryEnabled) return;
  setUser(null);
}

export { ErrorBoundary as SentryBoundary, captureException };
